// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    PartnerGroup,
    PartnerGroupPerson,
    SinglePartnerResourceByEmailRequest,
    UpdatePartnerGroupAdminPayload,
    UpdatePartnerGroupAdminResponse,
    UpdatePartnerGroupCoachPayload,
    UpdatePartnerGroupCoachResponse,
} from "./types/enterpriseNetworkPartnerTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedEnterpriseNetworkPartnerApi = createApi({
    reducerPath: "cclTokenedEnterpriseNetworkPartnerApi",
    tagTypes: [
        "AllPartnerResources",
        "SinglePartnerResource",
        "AllPartnerGroups",
        "PartnerResources",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getPartnerGroups: builder.query<PartnerGroup[], void>({
            query: () => "enterprise-network-partners/PartnerGroup",
            providesTags: ["AllPartnerGroups"],
        }),
        getAllPartnerResources: builder.query<PartnerGroupPerson[], void>({
            query: () => "enterprise-network-partners/PartnerGroup/all/people",
            providesTags: ["AllPartnerResources"],
        }),
        getPartnerResourceByEmail: builder.query<PartnerGroupPerson[], string>({
            query: (email) =>
                `enterprise-network-partners/PartnerGroup/all/person/${encodeURIComponent(email)}`,
            providesTags: (_, __, email) => [{ type: "SinglePartnerResource", id: email }],
        }),
        getSinglePartnerResources: builder.query<PartnerGroupPerson[], string>({
            query: (partnerId) => `enterprise-network-partners/PartnerGroup/${partnerId}/people`,
            providesTags: (_, __, partnerId) => [{ type: "PartnerResources", id: partnerId }],
        }),
        getSinglePartnerResourceByEmail: builder.query<
            PartnerGroupPerson,
            SinglePartnerResourceByEmailRequest
        >({
            query: (request) =>
                `enterprise-network-partners/PartnerGroup/${
                    request.partnerId
                }/person/${encodeURIComponent(request.email)}`,
            providesTags: (_, __, request) => [
                { type: "SinglePartnerResource", id: `${request.partnerId}:${request.email}` },
            ],
        }),
        addPartnerAdmin: builder.mutation<
            UpdatePartnerGroupAdminResponse,
            UpdatePartnerGroupAdminPayload
        >({
            query: (payload) => ({
                url: `enterprise-network-partners/PartnerGroupIndividuals`,
                method: "PUT",
                body: payload,
            }),
            invalidatesTags: (_, __, request) => [
                "AllPartnerResources",
                { type: "PartnerResources", id: request.partnerGroupKey ?? undefined },
            ],
        }),
        updatePartnerAdmin: builder.mutation<
            UpdatePartnerGroupAdminResponse,
            UpdatePartnerGroupAdminPayload
        >({
            query: (payload) => ({
                url: `enterprise-network-partners/PartnerGroupIndividuals/${payload.imKey}/${payload.partnerGroupKey}`,
                method: "POST",
                body: payload,
            }),
            invalidatesTags: (_, __, request) => [
                "AllPartnerResources",
                { type: "PartnerResources", id: request.partnerGroupKey ?? undefined },
            ],
        }),
        addPartnerCoach: builder.mutation<
            UpdatePartnerGroupCoachResponse,
            UpdatePartnerGroupCoachPayload
        >({
            query: (payload) => ({
                url: `enterprise-network-partners/PartnerGroupCoaches`,
                method: "PUT",
                body: payload,
            }),
            invalidatesTags: (_, __, request) => [
                "AllPartnerResources",
                { type: "PartnerResources", id: request.partnerGroupKey ?? undefined },
            ],
        }),
        updatePartnerCoach: builder.mutation<
            UpdatePartnerGroupCoachResponse,
            UpdatePartnerGroupCoachPayload
        >({
            query: (payload) => ({
                url: `enterprise-network-partners/PartnerGroupCoaches/${payload.imKey}/${payload.partnerGroupKey}`,
                method: "POST",
                body: payload,
            }),
            invalidatesTags: (_, __, request) => [
                "AllPartnerResources",
                { type: "PartnerResources", id: request.partnerGroupKey ?? undefined },
            ],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetPartnerGroupsQuery,
    useGetAllPartnerResourcesQuery,
    useLazyGetPartnerResourceByEmailQuery,
    useGetSinglePartnerResourcesQuery,
    useAddPartnerAdminMutation,
    useUpdatePartnerAdminMutation,
    useAddPartnerCoachMutation,
    useUpdatePartnerCoachMutation,
} = cclTokenedEnterpriseNetworkPartnerApi;
